import { useState, useEffect, useRef } from "react";
import { checkMfaStatus, sendMfaChallenge, sendPasscode } from "../api/mfa";
import { emailToUser } from "../api/email";
import { idValidationUrl } from "../config";
import { POLLING_INTERVAL, MAX_RETRY_COUNT } from "../constants/constants";
import { useStatusMessage } from "../contexts/StatusMessageContext";
import { useApiActivity } from "../contexts/ApiActivityContext";
import type { MfaActiveData } from "../types/mfa";

export type MfaPhase =
  | "idle"
  | "sending"
  | "polling"
  | "awaiting_passcode"
  | "success"
  | "error";

type InternalPhase = MfaPhase | "sending_email" | "submitting_passcode";

const DIRECT_PASSCODE_FACTOR_TYPES = new Set([
  "totp",
  "token:software:totp",
  "token:hardware",
  "hardware_token",
]);

const CHALLENGE_PASSCODE_FACTOR_TYPES = new Set([
  "sms",
  "call",
  "email",
]);

const SITE_VALIDATION_FACTOR_TYPES = new Set([
  "webauthn",
  "external_idp",
  "signed_nonce",
]);

export interface UseMfaVerificationReturn {
  phase: MfaPhase;
  sessionId: string | null;
  start: (factor: MfaActiveData) => void;
  submitPasscode: (code: string) => Promise<void>;
  reset: () => void;
}

const useMfaVerification = (): UseMfaVerificationReturn => {
  const [internalPhase, setInternalPhase] = useState<InternalPhase>("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeFactor, setActiveFactor] = useState<MfaActiveData | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runIdRef = useRef(0);
  const activityActiveRef = useRef(false);
  const { setStatus } = useStatusMessage();
  const { beginActivity, endActivity } = useApiActivity();

  const publicPhase: MfaPhase =
    internalPhase === "sending_email"
      ? "polling"
      : internalPhase === "submitting_passcode"
        ? "sending"
        : internalPhase;

  const endMfaActivity = () => {
    if (activityActiveRef.current) {
      activityActiveRef.current = false;
      endActivity("mfa");
    }
  };

  const beginMfaActivity = () => {
    if (!activityActiveRef.current) {
      activityActiveRef.current = true;
      beginActivity("mfa");
    }
  };

  const finishWithError = (message: string, runId = runIdRef.current) => {
    if (runId !== runIdRef.current) return;
    setStatus({ message, isError: true, isOkButtonVisible: true });
    setInternalPhase("error");
    endMfaActivity();
  };

  const finishWithSuccess = (message: string, runId = runIdRef.current) => {
    if (runId !== runIdRef.current) return;
    setStatus({ message, isError: false, isOkButtonVisible: true });
    setInternalPhase("success");
    endMfaActivity();
  };

  const start = (factor: MfaActiveData) => {
    if (internalPhase !== "idle") return;

    runIdRef.current += 1;
    setActiveFactor(factor);

    if (DIRECT_PASSCODE_FACTOR_TYPES.has(factor.factorType)) {
      setInternalPhase("awaiting_passcode");
      return;
    }

    beginMfaActivity();
    setInternalPhase("sending");
  };

  const reset = () => {
    runIdRef.current += 1;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    endMfaActivity();
    setInternalPhase("idle");
    setSessionId(null);
    setActiveFactor(null);
  };

  // Handle "sending" phase for push and other MFA types
  useEffect(() => {
    if (internalPhase !== "sending" || !activeFactor) return;

    const runId = runIdRef.current;
    const run = async () => {
      const { factorType, factorName, userId, factorId, userFirstName } = activeFactor;

      try {
        if (factorType === "push") {
          setStatus({ message: `Sending OktaVerify Push to ${factorName}...`, isError: false });

          const { data, error } = await sendMfaChallenge(factorType, userId, factorId);
          if (runId !== runIdRef.current) return;

          if (error) {
            finishWithError(error, runId);
            return;
          }

          const sid = (data as any)?.session_id;
          if (!sid) {
            finishWithError("MFA challenge did not return a session id", runId);
            return;
          }

          setSessionId(sid);
          setInternalPhase("polling");
        } else if (CHALLENGE_PASSCODE_FACTOR_TYPES.has(factorType)) {
          setStatus({ message: `Sending challenge to ${factorName}...`, isError: false });

          const { data, error } = await sendMfaChallenge(factorType, userId, factorId);
          if (runId !== runIdRef.current) return;

          if (error) {
            finishWithError(error, runId);
            return;
          }

          const sid = (data as any)?.session_id;
          if (!sid) {
            finishWithError("MFA challenge did not return a session id", runId);
            return;
          }

          setSessionId(sid);
          setInternalPhase("awaiting_passcode");
          endMfaActivity();
        } else if (SITE_VALIDATION_FACTOR_TYPES.has(factorType)) {
          setStatus({ message: `Sending challenge to ${factorName}...`, isError: false });

          const { data, error } = await sendMfaChallenge(factorType, userId, factorId);
          if (runId !== runIdRef.current) return;

          if (error) {
            if (error.includes("already in a validation group")) {
              finishWithError(
                `${userFirstName} already has a password change in progress. Please try again later.`,
                runId
              );
            } else {
              finishWithError(`*** Unexpected error occurred: ${error}`, runId);
            }
            return;
          }

          if (data) {
            const message = `Please advise ${userFirstName} to go to this website to complete identity validation:<br>
                              <b>${idValidationUrl}</b>`;
            setStatus({ message, isError: false, isOkButtonVisible: true });
            setInternalPhase("success");
            endMfaActivity();
          } else {
            finishWithError("MFA challenge returned an empty response", runId);
          }
        } else {
          finishWithError(`Unsupported factor type: ${factorType}`, runId);
        }
      } catch (error) {
        finishWithError(`Error verifying MFA: ${error}`, runId);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalPhase, activeFactor]);

  // Handle "polling" phase for push MFA
  useEffect((): (() => void) | void => {
    if (internalPhase !== "polling" || !activeFactor || !sessionId) return;

    const { factorType, userId, factorId } = activeFactor;
    let retryCount = 0;

    intervalRef.current = setInterval(() => {
      void (async (): Promise<void> => {
        const runId = runIdRef.current;
        let response;
        try {
          response = await checkMfaStatus(factorType, userId, factorId, sessionId);
        } catch (error) {
          finishWithError(`Error checking MFA status: ${error}`, runId);
          return;
        }
        if (runId !== runIdRef.current) return;

        if (response.errorMsg) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          if (response.errorMsg.includes("TIMEOUT")) {
            finishWithError("Validation timed out. Please try again", runId);
          } else {
            finishWithError(response.errorMsg, runId);
          }
          return;
        }

        if (response.status === "waiting") {
          setStatus({ message: "Waiting for user to accept identity verification...", isError: false });
        } else {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;

          if (response.status === "accepted") {
            setStatus({ message: "User has accepted OktaVerify!", isError: false });
            setInternalPhase("sending_email");
          } else {
            finishWithError(`Unknown status: ${response.status}`, runId);
          }
          return;
        }

        retryCount++;
        if (retryCount >= MAX_RETRY_COUNT) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          finishWithError("MFA check max retry exceeded", runId);
        }
      })();
    }, POLLING_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalPhase, sessionId, activeFactor]);

  // Handle post-push email send
  useEffect(() => {
    if (internalPhase !== "sending_email" || !activeFactor || !sessionId) return;

    const runId = runIdRef.current;
    const run = async () => {
      try {
        const { message, error } = await emailToUser(activeFactor.userEmail, sessionId);
        if (error) {
          finishWithError(error, runId);
        } else {
          finishWithSuccess(message || "", runId);
        }
      } catch (error) {
        finishWithError(`Error sending MFA email: ${error}`, runId);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalPhase, activeFactor, sessionId]);

  const submitPasscode = async (code: string): Promise<void> => {
    if (!activeFactor || internalPhase !== "awaiting_passcode") return;
    const { userId, factorType, factorId, userEmail } = activeFactor;
    const runId = runIdRef.current;

    beginMfaActivity();
    setStatus({ message: "Checking the validity of the code...", isError: false });
    setInternalPhase("submitting_passcode");

    try {
      const response = await sendPasscode(userId, factorType, factorId, code, sessionId ?? "");
      if (runId !== runIdRef.current) return;

      if (response.errorMsg) {
        setStatus({ message: response.errorMsg, isError: true, isOkButtonVisible: true });
        setInternalPhase("awaiting_passcode");
        endMfaActivity();
        return;
      }

      if (!response.sessionId) {
        finishWithError("MFA passcode verification did not return a session id", runId);
        return;
      }

      const sid = response.sessionId;
      setSessionId(sid);
      setStatus({ message: `Emailing magic link to ${activeFactor.userFirstName}...`, isError: false });
      const { message, error } = await emailToUser(userEmail, sid);
      if (runId !== runIdRef.current) return;
      if (error) {
        finishWithError(error, runId);
      } else {
        finishWithSuccess(message || "", runId);
      }
    } catch (error) {
      finishWithError(`Error submitting MFA passcode: ${error}`, runId);
    }
  };

  return { phase: publicPhase, sessionId, start, submitPasscode, reset };
};

export default useMfaVerification;
